import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, signal } from '@angular/core';
import { ICouponUse, IPackage } from '../../types';
import { Duration } from 'luxon';
import { MatDividerModule } from '@angular/material/divider';
import { PaintDirective } from '../../paint.directive';
import { CurrencyPipe } from '@angular/common';

@Component({
  selector: 'cart-summary',
  templateUrl: './cart-summary.component.html',
  styleUrls: ['./cart-summary.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatDividerModule, PaintDirective, CurrencyPipe]
})
export class CartSummaryComponent {
  destroy = inject(DestroyRef);

  package = input.required<IPackage>();
  use = input<ICouponUse|null>();
  retail = input<boolean>(false);

  units = {
    day: 1,
    week: 7,
    month: 30,
    year: 365
  }

  unit = computed(() => {
    return this.package()?.unit ?? 'unknown';
  });

  limited = computed(() => {
    return this.package()?.limited ?? false;
  });

  free_forever = computed(() => {
    const pkg = this.package();
    return !this.package || (!pkg?.price && (!pkg?.unit || pkg?.limited));
  });

  free_days = computed(() => {
    return this.use()?.coupon.free_days??0;
  })

  retailable = computed(() => {
    const pkg = this.package();
    return pkg?.package_type == 'internal' || this.retail();
  });

  duration_string = computed(() => {
    const pkg = this.package();

    if(!pkg){
      return '';
    }

    if(pkg.package_type == 'corporate' && !pkg.limited){
      return '';
    }

    const free_days = this.free_days();

    if(free_days){
      const base_days = Duration.fromDurationLike({ [pkg.unit??'year']: pkg.duration??0}).as('days');
      const new_days = free_days;//example: 60 days for 2 for 1 monthly

      const unit_count = new_days/base_days;//example: 60/30 = 2 months

      const is_exact_unit = new_days % base_days == 0;//if unit_count is whole number

      if(is_exact_unit){
        return `${unit_count} ${pkg.unit}${unit_count == 1 ? '' : 's'}`;
      }

      return `${new_days} day${new_days == 1 ? '' : 's'}`;
    }

    return `${pkg.duration} ${pkg.unit}${pkg.duration == 1 ? '' : 's'}`;

  });

  is_trial = computed(() => {
    const use = this.use();
    return use?.coupon?.reduction == 100 && use.coupon.percentage && !use.coupon.bypass;
  });

  base_price = computed(() => {
    return this.package()?.price ?? '0.00';
  });

  base_price_for_duration = computed(() => {
    const duration_string = this.duration_string();
    const pkg = this.package();

    if(!duration_string) return this.base_price();

    const [duration, unit] = duration_string.split(' ');

    if(unit.startsWith('day')){
      const base_days = Duration.fromDurationLike({ [pkg?.unit??'year']: pkg?.duration??0}).as('days');

      const base_duration = Number(duration)/base_days;//example: 60/30 = 2 months

      return (Number(this.base_price()) * Number(base_duration)).toFixed(2);

    }

    return (Number(this.base_price()) * Number(duration)).toFixed(2);

  });

  discounted_price = computed(() => {
    const use = this.use();
    const pkg = this.package();

    const usePackage = use?.coupon.package;

    if(!usePackage || usePackage != pkg?._id)
      return this.base_price();
    else{
      if(use.coupon.percentage){
        return (Number(pkg.price || 0) * ((100-use.coupon.reduction)/100)).toFixed(2); 
      }
      else{
        return (Number(pkg.price || 0) - use.coupon.reduction).toFixed(2);
      }
    }
  });

  is_discounted = computed(() => {
    return this.base_price() != this.discounted_price();
  });

  is_free = computed(() => {
    return Number(this.discounted_price()??'0.00') === 0;
  })

  discount_amount = computed(() => {
    return (Number(this.base_price_for_duration()) - Number(this.discounted_price())).toFixed(2);
  });

  no_card_required = computed(() => {
    return this.use()?.coupon.bypass;
  })

  plans = computed(() => {
    const pkg = this.package();
    if(!pkg) return ['premium'];

    return pkg.plans.filter(plan => ['global', pkg.slug].includes(plan));
  });

  // constructor(public ss: SharedService, private changes: ChangeDetectorRef) { 
  //   toSignal(toObservable(this.plan).pipe(switchMap()))
  // }

  // get activePromo(){
  //   if(this.package && this.package.slug && this.ss.promos && this.ss.promos[this.package.slug])
  //     return this.ss.promos[this.package.slug];

  //   return null;
  // }

}
